import { WellyQuickReplies, wellyGreeting } from "@/components/WellyQuickReplies";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  MagnifyingGlass, CaretLeft, ChatCircleDots, PaperPlaneRight, Question, Scales,
  ShieldCheck, UsersThree, Briefcase, User, Buildings, Lifebuoy, WarningOctagon,
} from "@phosphor-icons/react";

const COMPANY = "Sykurs Mester Skredder Lipasina ENK";
const ORG_NR = "824 770 492";

// Help content describes how Eligvia ACTUALLY works (eligibility-first, 4 layers,
// unknown stays unknown, private by default). Legal/contact specifics that need the owner
// are marked clearly rather than invented.
const CATEGORIES = [
  { id: "how", title: "How Eligvia works", icon: Lifebuoy },
  { id: "candidate", title: "For candidates", icon: User },
  { id: "employer", title: "For employers", icon: Briefcase },
  { id: "recruiter", title: "For recruiters / agencies", icon: Buildings },
  { id: "eligibility", title: "Eligibility & matching", icon: Scales },
  { id: "privacy", title: "Privacy & disclosure", icon: ShieldCheck },
  { id: "trust", title: "Reporting & trust", icon: WarningOctagon },
  { id: "account", title: "Account, data & GDPR", icon: UsersThree },
];

const ARTICLES = [
  { id: "a1", cat: "how", q: "What is Eligvia?",
    a: "Eligvia connects two sides of the market — people looking for work, and whoever has a job to fill (a direct employer, a hiring manager, or a recruiter/agency acting for a client). It is built eligibility-first: it shows you roles you can realistically apply for and always explains why a role fits or does not. One central engine evaluates every candidate↔job pair the same way, no matter who posted the job." },
  { id: "a2", cat: "how", q: "The three services",
    a: "There are three workspaces for three client groups, but one shared matching engine: Candidate (find jobs you can actually apply for), Employer (find candidates who qualify for a specific role), and Recruiter/Agency (manage multiple clients → their jobs → candidate pools → matching). The workflow and permissions differ; the matching logic does not." },
  { id: "a3", cat: "eligibility", q: "Eligibility vs matching — what's the difference?",
    a: "Eligibility is a GATE: can you apply at all (e.g. work authorization, residence rules, licences). It is decided by deterministic rules, not an opaque AI score. Matching then describes how well you fit. A role can be a great match but ineligible if a mandatory eligibility rule fails — and we always show the reasons." },
  { id: "a4", cat: "eligibility", q: "PASS / FAIL / UNKNOWN / FLEX — what do they mean?",
    a: "PASS: you meet the rule. FAIL: a hard rule is not met (shown clearly, with why). UNKNOWN: the advert or your profile doesn't state enough — this never counts against you; unknown stays unknown. FLEX USED: you passed inside a flexible boundary you set (e.g. you accept up to 2 office days/week)." },
  { id: "a5", cat: "eligibility", q: "Capability, Preference, Practical and Overall",
    a: "Beyond eligibility we show four separate layers, each with a 'why': Capability (can you do the job — skills, experience, education), Preference (do you want this kind of job), Practical (does it fit your situation — location, timezone, office, travel, relocation), and Overall, a ranking only (Capability 50% · Practical 30% · Preference 20%). Eligibility is never folded into that percentage, and a high Overall never overrides a hard blocker. If key information is missing, Overall is shown as 'provisional'." },
  { id: "a6", cat: "eligibility", q: "Remote, location, work authorization, sponsorship, relocation",
    a: "'Remote' is not a yes/no — we model worldwide / regional (EU ≠ EEA ≠ Europe ≠ Nordics) / named-country / domestic / hybrid, plus office presence, travel and timezone. If an advert only says 'remote' without stating where you may live, we keep it UNKNOWN rather than assuming worldwide. Work authorization, visa sponsorship and relocation are handled as distinct facts — we never guess your legal right to work." },
  { id: "a7", cat: "candidate", q: "Building your profile & uploading a CV",
    a: "Add your facts under Profile, or upload a CV — Eligvia extracts information for you to review and confirm before anything is saved. It never invents qualifications or language levels; anything unclear stays unknown. The more complete your profile, the fewer 'unknown' results you'll see." },
  { id: "a8", cat: "candidate", q: "Your Matches vs searching yourself",
    a: "You always have both: automatic 'Your Matches' generated from your profile, and your own manual search. The system helps you find roles — it never hides the rest of the market from you." },
  { id: "a9", cat: "employer", q: "Posting a role and seeing who qualifies",
    a: "Create roles in the Employer workspace. Candidates who opted in and pass your role's rules are surfaced as qualified / possibly / not eligible, each with the reason. You can mark each requirement as mandatory, preferred, flexible or unknown — marking something flexible softens it instead of hard-blocking." },
  { id: "a10", cat: "recruiter", q: "Clients → Jobs → Candidate Pools → Matching",
    a: "Recruiters manage multiple clients, each with their own roles, and reusable candidate pools (a candidate can be in several pools). You match a pool against a specific client job using the same central engine as direct employers, and can invite candidates — who stay in control of what they share back." },
  { id: "a11", cat: "privacy", q: "Candidate-controlled disclosure",
    a: "Candidates are private by default. Some details (e.g. salary expectation, relocation preferences, preferred countries) are used for matching but are NOT shown to employers unless you choose to share them — employers see the eligibility result (e.g. 'meets the location requirement'), not your raw numbers. Your contact and documents are shared only when you explicitly accept an invitation." },
  { id: "a12", cat: "trust", q: "Reporting a misleading job or a scam",
    a: "Every listing has a 'Report this listing' option. Flag misleading adverts (e.g. 'remote' that actually requires residence somewhere), problems with an employer, or scams (anyone asking for upfront payment is a red flag). Reports are triaged automatically and escalated to our team; scam reports are treated as highest priority." },
  { id: "a13", cat: "account", q: "Your data, export and deletion (GDPR)",
    a: "You can export your data and request deletion from Settings. We store only what's needed to run the service and to match you. Contacting support does not sign you up for marketing — newsletter consent is always a separate, optional choice you can withdraw at any time." },
  { id: "a14", cat: "account", q: "Plans & billing",
    a: "Business accounts start a trial and can choose a plan. Eligibility accuracy is never limited by plan — plans differ only in volume and tools. Online payment is being finalised." },
];

export default function HelpCenter() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState(null);
  const [open, setOpen] = useState(null); // article id

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = ARTICLES;
    if (cat) list = list.filter((a) => a.cat === cat);
    if (q) list = ARTICLES.filter((a) => (a.q + " " + a.a).toLowerCase().includes(q));
    return list;
  }, [query, cat]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-slate-900 text-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-6 py-10 sm:py-14">
          <Link to="/" className="text-sm text-slate-300 hover:text-white flex items-center gap-1.5 mb-6"><CaretLeft /> Back to Eligvia</Link>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight" data-testid="help-title">How can we help?</h1>
          <p className="mt-3 text-slate-300 text-lg max-w-2xl">Learn how Eligvia works, what the results mean, and how to get the most out of it — whether you're looking for work or hiring.</p>
          <div className="mt-6 relative max-w-xl">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => { setQuery(e.target.value); setOpen(null); }} data-testid="help-search"
              placeholder="Search help articles…" className="pl-10 h-12 bg-white text-slate-900" />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-5 sm:px-6 py-8 sm:py-10 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Categories */}
        <aside className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-slate-400 font-medium mb-2">Topics</p>
          <nav className="flex gap-2 overflow-x-auto pb-1 lg:block lg:space-y-1 lg:overflow-visible lg:pb-0" data-testid="help-categories">
            <button onClick={() => { setCat(null); setOpen(null); }} className={`shrink-0 lg:w-full text-left text-sm rounded-md px-3 py-2 whitespace-nowrap ${!cat ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>All topics</button>
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <button key={c.id} onClick={() => { setCat(c.id); setQuery(""); setOpen(null); }} data-testid={`help-cat-${c.id}`}
                  className={`shrink-0 lg:w-full text-left text-sm rounded-md px-3 py-2 whitespace-nowrap lg:whitespace-normal flex items-center gap-2 ${cat === c.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  <Icon weight="fill" className="shrink-0 opacity-70" /> {c.title}
                </button>
              );
            })}
          </nav>
          <div className="mt-4 lg:mt-6 flex flex-row flex-wrap gap-x-4 gap-y-1 lg:flex-col text-sm">
            <Link to="/terms" className="text-slate-600 hover:text-slate-900" data-testid="help-terms-link">Terms of Service</Link>
            <Link to="/privacy" className="text-slate-600 hover:text-slate-900" data-testid="help-privacy-link">Privacy Policy</Link>
            <Link to="/rules" className="text-slate-600 hover:text-slate-900" data-testid="help-rules-link">User Rules</Link>
          </div>
        </aside>

        {/* Articles */}
        <main className="min-w-0">
          <p className="text-sm text-slate-500 mb-3">{results.length} article{results.length === 1 ? "" : "s"}{cat ? ` · ${CATEGORIES.find((c) => c.id === cat)?.title}` : ""}</p>
          <div className="space-y-2" data-testid="help-articles">
            {results.length === 0 && <p className="text-sm text-slate-400">No articles found. Try the assistant below.</p>}
            {results.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-200 bg-white" data-testid={`help-article-${a.id}`}>
                <button onClick={() => setOpen(open === a.id ? null : a.id)} className="w-full text-left px-5 py-3.5 flex items-center justify-between gap-3">
                  <span className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Question className="text-slate-400" /> {a.q}</span>
                </button>
                {open === a.id && <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{a.a}</p>}
              </div>
            ))}
          </div>

          <HelpAssistant />
        </main>
      </div>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-6 py-6 text-xs text-slate-400">
          {COMPANY} · Org.nr {ORG_NR} · <a href="mailto:support@eligvia.com" className="underline" data-testid="help-contact-support">support@eligvia.com</a> ·
          <Link to="/terms" className="underline ml-1">Terms</Link> · <Link to="/privacy" className="underline">Privacy</Link> · <Link to="/rules" className="underline" data-testid="help-footer-rules-link">User Rules</Link>
        </div>
      </footer>
    </div>
  );
}

function HelpAssistant() {
  const [tab, setTab] = useState("chat"); // chat | contact
  const [sid] = useState(() => Math.random().toString(36).slice(2));
  const [msgs, setMsgs] = useState(() => [{ role: "assistant", content: wellyGreeting() }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  // contact form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState("candidate");
  const [company, setCompany] = useState("");
  const [cmsg, setCmsg] = useState("");
  const [newsletter, setNewsletter] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async (text) => {
    const m = (typeof text === "string" ? text : input).trim();
    if (!m || busy) return;
    if (typeof text !== "string") setInput(""); setMsgs((x) => [...x, { role: "user", content: m }]); setBusy(true);
    try {
      const r = await api.post("/support/public-chat", { session_id: sid, message: m });
      setMsgs((x) => [...x, { role: "assistant", content: r.data.answer }]);
    } catch (e) {
      setMsgs((x) => [...x, { role: "assistant", content: "I'm having trouble right now — please leave a message and we'll follow up." }]);
    } finally { setBusy(false); }
  };

  const submitLead = async () => {
    if (!name.trim() || !email.includes("@")) { toast.error("Please add your name and a valid email"); return; }
    setBusy(true);
    try {
      await api.post("/support/lead", { name, email, interest, company, message: cmsg, session_id: sid, newsletter_consent: newsletter });
      setSent(true); toast.success("Thanks — we'll be in touch.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-10 rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="help-assistant">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 sm:px-5 py-3">
        <ChatCircleDots weight="fill" className="text-slate-700" />
        <h2 className="font-bold text-slate-800">Still need help?</h2>
        <div className="sm:ml-auto flex gap-1 text-sm">
          <button onClick={() => setTab("chat")} data-testid="assistant-tab-chat" className={`rounded-md px-3 py-2 ${tab === "chat" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>Ask Welly</button>
          <button onClick={() => setTab("contact")} data-testid="assistant-tab-contact" className={`rounded-md px-3 py-2 ${tab === "contact" ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>Leave a message</button>
        </div>
      </div>

      {tab === "chat" ? (
        <div data-testid="assistant-chat">
          <div className="max-h-80 overflow-y-auto px-5 py-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} data-testid={`assistant-msg-${m.role === "user" ? "user" : "bot"}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] break-words whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>{m.content}</div>
              </div>
            ))}
            {msgs.length === 1 && <WellyQuickReplies disabled={busy} onPick={(q) => (q.id === "contact" ? setTab("contact") : send(q.message))} />}
            {busy && <p className="text-xs text-slate-400">Thinking…</p>}
            <div ref={endRef} />
          </div>
          <div className="flex gap-2 border-t border-slate-100 p-3">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask a question…" data-testid="assistant-input" />
            <Button onClick={() => send()} disabled={busy} data-testid="assistant-send" className="bg-slate-900 hover:bg-slate-800"><PaperPlaneRight weight="fill" /></Button>
          </div>
          <p className="px-5 pb-3 text-[11px] text-slate-400">Welly is Eligvia's automated help assistant. It answers from Eligvia's help content and never invents facts; if it can't help, it passes your request to a human.</p>
        </div>
      ) : (
        <div className="p-5" data-testid="assistant-contact">
          {sent ? (
            <p className="text-sm text-emerald-700" data-testid="lead-sent">Thank you, {name.split(" ")[0]}! We've received your message and will follow up by email.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="lead-name" />
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="lead-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <select value={interest} onChange={(e) => setInterest(e.target.value)} data-testid="lead-interest" className="rounded-md border border-slate-200 px-3 py-2 text-sm">
                  <option value="candidate">I'm looking for work</option>
                  <option value="employer">I'm hiring (employer)</option>
                  <option value="recruiter">I'm a recruiter / agency</option>
                  <option value="other">Something else</option>
                </select>
                <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company (optional)" data-testid="lead-company" />
              </div>
              <Textarea rows={3} value={cmsg} onChange={(e) => setCmsg(e.target.value)} placeholder="How can we help?" data-testid="lead-message" />
              <label className="flex items-start gap-2 text-xs text-slate-500">
                <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} data-testid="lead-newsletter" className="mt-0.5" />
                <span>I'd like to receive Eligvia news and useful updates by email. (Optional — separate from this request; you can unsubscribe any time.)</span>
              </label>
              <Button onClick={submitLead} disabled={busy} data-testid="lead-submit" className="bg-slate-900 hover:bg-slate-800">Send message</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
